import { auth } from './firebase';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Authenticated fetch wrapper. Attaches the current user's Firebase ID token.
 */
export async function apiFetch<T = unknown>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const user = auth.currentUser;
  if (!user) {
    throw new ApiError(401, 'Not signed in');
  }

  const token = await user.getIdToken();
  const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;

  const res = await fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init.headers || {}),
    },
  });

  if (!res.ok) {
    let message = `Request failed: ${res.status}`;
    try {
      const body = await res.json();
      message = body.message || body.error || message;
    } catch {
      // ignore non-JSON body
    }
    throw new ApiError(res.status, message);
  }

  return res.json() as Promise<T>;
}

// ---- Cost endpoints ----

export interface CostRow {
  cloud: string;
  accountId: string;
  month: string;
  env: string;
  service: string;
  sku: string;
  skuId?: string;
  projectId?: string;
  nativeCost: number;
  nativeCurrency: string;
  hkdCost: number;
  feature?: string;
  costCenter?: string;
}

export interface TotalCostResponse {
  data: CostRow[];
  cachedAt: string;
  warnings: Record<string, string>;
}

export interface BillingAccount {
  cloud: string;
  accountId: string;
  label?: string;
}

export function getBillingAccounts(): Promise<{ accounts: BillingAccount[] }> {
  return apiFetch('/api/costs/accounts');
}

export function getFeatures(): Promise<{ features: string[] }> {
  return apiFetch('/api/costs/features');
}

export function getTotalCosts(params: {
  month: string;
  env?: string;
  costCenter?: string;
  accountId?: string;
  refresh?: boolean;
}): Promise<TotalCostResponse> {
  const search = new URLSearchParams({ month: params.month });
  if (params.env) search.set('env', params.env);
  if (params.costCenter) search.set('costCenter', params.costCenter);
  if (params.accountId) search.set('accountId', params.accountId);
  if (params.refresh) search.set('refresh', 'true');
  return apiFetch(`/api/costs/total?${search.toString()}`);
}

export interface TrendMonth {
  month: string; // "YYYY-MM"
  hkdTotal: number;
  byCloud: Record<string, number>;
}

export interface TrendResponse {
  months: TrendMonth[];
  warnings: Record<string, string>;
}

export function getCostTrend(params: {
  months?: number;
  env?: string;
  costCenter?: string;
  accountId?: string;
  refresh?: boolean;
}): Promise<TrendResponse> {
  const search = new URLSearchParams();
  if (params.months) search.set('months', String(params.months));
  if (params.env) search.set('env', params.env);
  if (params.costCenter) search.set('costCenter', params.costCenter);
  if (params.accountId) search.set('accountId', params.accountId);
  if (params.refresh) search.set('refresh', 'true');
  return apiFetch(`/api/costs/trend?${search.toString()}`);
}
